import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { FileText, Loader2, Clock, User, Search, Send, ArrowLeft } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function DevisAEnvoyerPage() {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [filterCommercial, setFilterCommercial] = useState(null);
    const [avgTimeData, setAvgTimeData] = useState({});
    const [allCommerciaux, setAllCommerciaux] = useState([]);

    const fetchData = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API}/api/inventaires/devis-a-envoyer`, { withCredentials: true });
            setItems(data);
        } catch {
            setItems([]);
        } finally { setLoading(false); }
    }, []);

    useEffect(() => {
        fetchData();
        axios.get(`${API}/api/inventaires/devis-a-envoyer/avg-time`, { withCredentials: true })
            .then(({ data }) => setAvgTimeData(data))
            .catch(() => {});
        axios.get(`${API}/api/inventaires/config`, { withCredentials: true })
            .then(({ data }) => setAllCommerciaux(data.commerciaux || []))
            .catch(() => {});
    }, [fetchData]);

    // Build commercial stats: start from all commercials, then count items
    const commercialList = (() => {
        const stats = {};
        // Init all commercials with 0
        allCommerciaux.forEach(c => {
            const hist = avgTimeData[c.id] || {};
            stats[c.id] = {
                id: c.id,
                name: c.name,
                couleur: c.couleur || '#94a3b8',
                count: 0,
                avgElapsed: hist.avg_elapsed || '-',
                histCount: hist.count || 0,
            };
        });
        // Count items per commercial
        items.forEach(item => {
            const key = item.commercial_id || '_none';
            if (!stats[key]) {
                const hist = avgTimeData[key] || {};
                stats[key] = {
                    id: item.commercial_id,
                    name: item.commercial_name || 'Non assigné',
                    couleur: item.commercial_couleur || '#94a3b8',
                    count: 0,
                    avgElapsed: hist.avg_elapsed || '-',
                    histCount: hist.count || 0,
                };
            }
            stats[key].count++;
        });
        return Object.values(stats).sort((a, b) => b.count - a.count);
    })();

    const filtered = items.filter(item => {
        const matchSearch = !search.trim() || (item.client_nom || '').toLowerCase().includes(search.toLowerCase());
        const matchCommercial = !filterCommercial || (item.commercial_id || '_none') === filterCommercial;
        return matchSearch && matchCommercial;
    });

    return (
        <div className="p-4 md:p-6 lg:p-8" data-testid="devis-a-envoyer-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
                <div className="flex items-center gap-3">
                    <Button variant="outline" size="sm" onClick={() => navigate('/devis')} data-testid="dae-back-btn">
                        <ArrowLeft size={16} className="mr-1.5" />
                        Retour
                    </Button>
                    <div>
                        <h1
                            className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight"
                            style={{ fontFamily: 'Manrope, sans-serif' }}
                            data-testid="dae-title"
                        >
                            Devis à envoyer
                        </h1>
                        <p className="mt-1 text-slate-500">
                            {items.length} client{items.length > 1 ? 's' : ''} en attente
                        </p>
                    </div>
                </div>
            </div>

            {/* Commercial cards */}
            {commercialList.length > 0 && (
                <div className={`grid gap-3 mb-6`} style={{ gridTemplateColumns: `repeat(${commercialList.length}, 1fr)` }} data-testid="dae-commercial-stats">
                    {commercialList.map(c => (
                        <Card
                            key={c.id || '_none'}
                            className={`rounded-lg border shadow-sm cursor-pointer transition-all hover:shadow-md ${
                                filterCommercial === (c.id || '_none')
                                    ? 'ring-2 ring-offset-1 bg-slate-50'
                                    : 'bg-white border-slate-200'
                            }`}
                            style={filterCommercial === (c.id || '_none') ? { borderColor: c.couleur } : {}}
                            onClick={() => setFilterCommercial(prev => prev === (c.id || '_none') ? null : (c.id || '_none'))}
                            data-testid={`dae-commercial-card-${c.id || 'none'}`}
                        >
                            <CardContent className="px-4 py-3 flex items-center gap-2.5">
                                <div
                                    className="w-9 h-9 rounded-md flex items-center justify-center shrink-0"
                                    style={{ backgroundColor: c.couleur + '20' }}
                                >
                                    <span className="text-sm font-bold" style={{ color: c.couleur }}>{c.count}</span>
                                </div>
                                <div className="min-w-0 flex-1">
                                    <p className="text-sm font-semibold text-slate-800 truncate">{c.name}</p>
                                    <p className="text-xs text-red-500 font-medium flex items-center gap-1">
                                        <Clock size={11} /> moy. {c.avgElapsed}
                                    </p>
                                    {c.histCount > 0 && <p className="text-[11px] text-red-400 font-medium">({c.histCount} envois)</p>}
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}

            {/* Search */}
            <Card className="bg-white rounded-xl border border-slate-200 shadow-sm mb-6">
                <CardContent className="p-4">
                    <div className="relative flex-1">
                        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <Input
                            placeholder="Rechercher par nom..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="pl-10"
                            data-testid="dae-search"
                        />
                    </div>
                </CardContent>
            </Card>

            {/* Table */}
            <Card className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg font-semibold text-slate-800">
                        <FileText size={20} className="text-[#2e7cd6]" />
                        Devis à envoyer ({filtered.length})
                    </CardTitle>
                </CardHeader>
                <CardContent className="p-0 sm:p-6">
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="h-8 w-8 animate-spin text-[#2e7cd6]" />
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="text-center py-12 text-slate-500">
                            Aucun devis en attente d'envoi
                        </div>
                    ) : (
                        <div className="overflow-x-auto -mx-0">
                            <Table data-testid="dae-table" className="min-w-[600px]">
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Nom</TableHead>
                                        <TableHead>Commercial</TableHead>
                                        <TableHead>Temps écoulé</TableHead>
                                        <TableHead className="text-right"></TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filtered.map(item => (
                                        <TableRow key={item.inventaire_id} data-testid={`dae-row-${item.inventaire_id}`}>
                                            <TableCell>
                                                <div className="flex items-center gap-3">
                                                    <div className="w-9 h-9 rounded-full bg-blue-50 flex items-center justify-center shrink-0">
                                                        <Send size={16} className="text-[#2e7cd6]" />
                                                    </div>
                                                    <p className="font-medium text-slate-900">{item.client_nom || '-'}</p>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                {item.commercial_name ? (
                                                    <span
                                                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium text-white"
                                                        style={{ backgroundColor: item.commercial_couleur || '#94a3b8' }}
                                                    >
                                                        {item.commercial_name}
                                                    </span>
                                                ) : (
                                                    <span className="text-sm text-slate-400 italic">Non assigné</span>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-1.5">
                                                    <Clock size={14} className="text-amber-500" />
                                                    <span className="text-sm font-medium text-amber-700">{item.elapsed}</span>
                                                </div>
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <Button
                                                    size="sm"
                                                    className="bg-[#2e7cd6] hover:bg-[#2568b8] text-white"
                                                    onClick={() => navigate(`/inventaires?open=${item.inventaire_id}`)}
                                                    data-testid={`dae-envoyer-${item.inventaire_id}`}
                                                >
                                                    <Send size={14} className="mr-1.5" />
                                                    Envoyer
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
